import { Card } from '@/types/Card';
import {
  CARD_KEYS,
  CHANNEL_KEYS,
  CHANNEL_NAMES,
  BANK_KEYS,
  BANK_NAMES,
} from '../../constants/keys';
import { FeatureType } from '@/types/Feature';
import { CardLinkMapping, BankLinkMapping } from '../cardLinks';

/**
 * Map of bank keys to their source URLs
 * This allows us to easily update bank information when they change their websites
 * The key is the bank key from BANK_KEYS
 */
export const bankLinkMap: Record<string, BankLinkMapping> = {
  [BANK_KEYS.ESUN]: {
    sourceUrl: 'https://www.esunbank.com/zh-tw/personal/credit-card/intro',
    lastUpdated: '2025-05-04',
  },
};

/**
 * Map of card keys to their source URLs
 * This allows us to easily update card information when banks change their offerings
 * The key is the card key from CARD_KEYS
 */
export const cardLinkMap: Record<string, CardLinkMapping> = {
  // E.SUN Bank cards
  [CARD_KEYS.ESUN_PI]: {
    sourceUrl: 'https://www.esunbank.com/zh-tw/personal/credit-card/intro/co-branded-card/pi-card',
    applyUrl:
      'https://card.esunbank.com.tw/EsunCreditweb/txnproc/selApplyCard?PRJCD=APYCRD0049&param=981747190906876',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.ESUN_UBEAR]: {
    sourceUrl: 'https://www.esunbank.com/zh-tw/personal/credit-card/intro/bank-card/u-bear',
    applyUrl:
      'https://card.esunbank.com.tw/EsunCreditweb/txnproc/selApplyCard?PRJCD=APYCRD0049&param=981747190906889',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.ESUN_KUMAMON]: {
    sourceUrl: 'https://www.esunbank.com/zh-tw/personal/credit-card/intro/bank-card/kumamon_card',
    applyUrl:
      'https://card.esunbank.com.tw/EsunCreditweb/txnproc/selApplyCard?PRJCD=APYCRD0069&param=531747202358769',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.ESUN_UNICARD]: {
    sourceUrl: 'https://www.esunbank.com/zh-tw/personal/credit-card/intro/bank-card/unicard',
    applyUrl:
      'https://card.esunbank.com.tw/EsunCreditweb/txnproc/selApplyCard?PRJCD=APYCRD0070&param=531747202358759',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.ESUN_STARLUX_TITANIUM]: {
    sourceUrl:
      'https://www.esunbank.com/zh-tw/personal/credit-card/intro/co-branded-card/starlux-card',
    applyUrl:
      'https://card.esunbank.com.tw/EsunCreditweb/txnproc/selApplyCard?PRJCD=APYCRD0067&param=641747201840899',
    lastUpdated: '2025-05-16',
  },
  [CARD_KEYS.ESUN_STARLUX_BUSINESS]: {
    sourceUrl:
      'https://www.esunbank.com/zh-tw/personal/credit-card/intro/co-branded-card/starlux-card',
    applyUrl:
      'https://card.esunbank.com.tw/EsunCreditweb/txnproc/selApplyCard?PRJCD=APYCRD0067&param=641747201840899',
    lastUpdated: '2025-05-16',
  },
  [CARD_KEYS.ESUN_STARLUX_WORLD]: {
    sourceUrl:
      'https://www.esunbank.com/zh-tw/personal/credit-card/intro/co-branded-card/starlux-card',
    applyUrl:
      'https://card.esunbank.com.tw/EsunCreditweb/txnproc/selApplyCard?PRJCD=APYCRD0067&param=641747201840899',
    lastUpdated: '2025-05-16',
  },
  [CARD_KEYS.ESUN_STARLUX_WORLD_SUPREME]: {
    sourceUrl:
      'https://www.esunbank.com/zh-tw/personal/credit-card/intro/co-branded-card/starlux-card',
    applyUrl:
      'https://card.esunbank.com.tw/EsunCreditweb/txnproc/selApplyCard?PRJCD=APYCRD0067&param=641747201840899',
    lastUpdated: '2025-05-16',
  },
};

export const esunCards: { [key: string]: Card } = {
  [CARD_KEYS.ESUN_UBEAR]: {
    name: '玉山U Bear信用卡',
    summary: '網購最高3%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.ESUN],
    payment_network: 'Mastercard',
    picture: 'logo_esun-ubear.webp',
    key: 'esun-ubear',
    link: cardLinkMap[CARD_KEYS.ESUN_UBEAR].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.ESUN_UBEAR].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '6% 現金',
        max_feedback: '每期回饋上限100元，於當期帳單直接折抵',
        simplified_condition: '指定娛樂/影音平台消費享10%現金回饋',
        condition:
          '指定娛樂/影音平台包含Disney+、Nintendo、PlayStation、Netflix、Spotify等串流平台',
        channel: '娛樂/影音平台',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 現金',
        max_feedback: '每月最高回饋400元',
        simplified_condition: '行動支付、網購最高10%回饋',
        condition:
          '網購最高享3%現金回饋 (含基本回饋1%+網購加碼2%)，加碼2%需綁定帳單e化。網路消費包含國內外線上消費 (例如：momo購物網、蝦皮、Coupang 酷澎、淘寶、線上訂房、高鐵線上購票等)、行動支付 (例如：LINE Pay、街口支付等)',
        channel: '網購',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% 現金',
        max_feedback: '回饋無上限',
        simplified_condition: '國內外一般消費享1%現金回饋',
        condition: '國內外一般消費享1%現金回饋，無上限',
        channel: '國內外一般消費',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '1%',
    specific_channel_feedback: '6%',
    start_date: '2025-01-01',
    end_date: '2025-08-31',
  },
  [CARD_KEYS.ESUN_PI]: {
    name: '玉山Pi拍錢包聯名卡',
    summary: '外送最高10%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.ESUN],
    payment_network: 'VISA',
    picture: 'logo_esun-pi.webp',
    key: 'esun-pi',
    link: cardLinkMap[CARD_KEYS.ESUN_PI].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.ESUN_PI].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '10% 現金',
        max_feedback: '每月最高回饋300元',
        simplified_condition: '指定外送平台',
        condition: '指定外送平台消費享10%現金回饋，每月最高回饋300元',
        channel: '外送',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '5% 現金',
        max_feedback: '每月最高回饋250元',
        simplified_condition: '一般餐廳',
        condition: '一般餐廳消費享5%現金回饋，每月最高回饋250元',
        channel: '餐飲',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1.5% 現金',
        max_feedback: '回饋無上限',
        simplified_condition: '一般消費',
        condition: '一般消費享1.5%現金回饋，無上限',
        channel: '國內一般消費',
      },
    ],
    domestic_feedback: '1.5%',
    international_feedback: '1.5%',
    specific_channel_feedback: '10%',
    start_date: '2025-01-01',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.ESUN_KUMAMON]: {
    name: '玉山熊本熊卡',
    summary: '指定日本商店8.5%優惠',
    issuing_bank: BANK_NAMES[BANK_KEYS.ESUN],
    payment_network: 'VISA',
    picture: 'logo_esun-kumamon.webp',
    key: 'esun-kumamon',
    link: cardLinkMap[CARD_KEYS.ESUN_KUMAMON].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.ESUN_KUMAMON].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '8.5% 優惠',
        max_feedback: '加碼6%每歸戶每期回饋上限500元',
        simplified_condition: '指定日本商店消費',
        condition:
          '指定日本商店消費最高享8.5%優惠，含日本一般消費1%+指定日本商店加碼6%+雙幣卡免收1.5%國外交易服務費',
        channel: '日本消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% 現金',
        max_feedback: '回饋無上限',
        simplified_condition: '日本一般消費',
        condition:
          '日本一般消費享1%現金回饋，回饋無上限，於當期帳單直接折扣，雙幣卡免收1.5%國外交易服務費',
        channel: '日本消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% 現金',
        max_feedback: '回饋無上限',
        simplified_condition: '國內一般消費',
        condition: '國內一般消費享1%現金回饋，回饋無上限，於當期帳單直接折扣',
        channel: '國內消費',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '2.5%',
    specific_channel_feedback: '8.5%',
    start_date: '2025-01-01',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.ESUN_UNICARD]: {
    name: '玉山Unicard',
    summary: '百大特店最高5%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.ESUN],
    payment_network: 'VISA',
    picture: 'logo_esun-unicard.webp',
    key: 'esun-unicard',
    link: cardLinkMap[CARD_KEYS.ESUN_UNICARD].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.ESUN_UNICARD].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% 玉山e point',
        max_feedback: '一般消費基本回饋',
        simplified_condition: '需申辦帳單e化及綁定玉山銀行臺幣帳戶自動扣繳',
        condition:
          '一般消費享0.3% 玉山e point回饋(需申辦帳單e化)，加碼享0.7% 玉山e point回饋(需同時申辦帳單e化及綁定玉山銀行臺幣帳戶自動扣繳)',
        channel: '一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3.5% 玉山e point',
        max_feedback: '百大特店加碼2%，每月上限500點',
        simplified_condition: '簡單選(核卡即享)',
        condition:
          '最高3.5% (一般消費1%無上限，百大特店加碼2%每月上限500點，新戶加碼0.5%上限500點)',
        channel: '百大特店',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '4% 玉山e point',
        max_feedback: '百大特店加碼2.5%，每月上限500點',
        simplified_condition: '任意選(玉山Wallet app 選定8家特店)',
        condition:
          '最高4% (一般消費1%無上限，百大特店加碼2.5%每月上限500點，新戶加碼0.5%上限500點)，中華航空、長榮航空、日本航空、台灣虎航、樂桃航空、酷航、立榮航空、華信航空、Trip.com、booking.com、Hotels.com、AsiaYo、Expedia、KKday、KLOOK、雄獅旅遊、可樂旅遊、東南旅行社',
        channel: '百大特店',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '5% 玉山e point',
        max_feedback: '百大特店加碼3.5%，每月上限500點',
        simplified_condition: 'UP選(完成任務或以99點玉山e point訂閱)',
        condition:
          '最高5% (一般消費1%無上限，百大特店加碼3.5%每月上限500點，新戶加碼0.5%上限500點)',
        channel: '百大特店',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '1%',
    specific_channel_feedback: '5%',
    start_date: '2025-01-01',
    end_date: '2025-09-30',
  },
  [CARD_KEYS.ESUN_STARLUX_TITANIUM]: {
    name: '玉山星宇航空聯名鈦金卡',
    summary: '最優NT$10=1哩，星宇航空消費最高NT$5=1哩，年費低',
    issuing_bank: BANK_NAMES[BANK_KEYS.ESUN],
    payment_network: 'VISA',
    picture: 'logo_esun-starlux-titanium.png',
    key: 'esun-starlux-titanium',
    link: cardLinkMap[CARD_KEYS.ESUN_STARLUX_TITANIUM].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.ESUN_STARLUX_TITANIUM].applyUrl,
    features: [
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$20=1哩',
        max_feedback: '無上限',
        simplified_condition: '國外消費',
        condition: '國外消費每NT$20可獲得1哩星宇航空COSMILE哩程，活動期間2025/3/1~2025/12/31',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$28=1哩',
        max_feedback: '無上限',
        simplified_condition: '國內消費',
        condition: '國內消費每NT$28可獲得1哩星宇航空COSMILE哩程，活動期間2025/3/1~2025/12/31',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.MILEAGE,
        feedback: '生日月2倍哩程',
        max_feedback: '無上限',
        simplified_condition: '生日月消費',
        condition: '持卡人於生日月份刷卡消費可獲得2倍哩程回饋，活動期間2025/3/1~2025/12/31',
        channel: '生日月消費',
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場外圍停車',
        max_feedback: '每年4次',
        simplified_condition: '每次最多7天',
        condition: '每年最多4次，每次最多7天免費機場外圍停車服務，須達消費門檻',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: '旅遊平安險',
        max_feedback: '最高2,000萬',
        simplified_condition: '公共運輸工具',
        condition:
          '支付旅途中全額公共運輸工具費用，或參加旅行團時刷卡支付80%以上之團費，享旅遊平安險最高2,000萬保障',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRAVEL],
      },
    ],
    domestic_feedback: 'NT$28=1哩',
    international_feedback: 'NT$20=1哩',
    specific_channel_feedback: 'NT$20=1哩',
    start_date: '2025-03-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.ESUN_STARLUX_BUSINESS]: {
    name: '玉山星宇航空聯名商務鈦金卡',
    summary: '最優NT$15=1哩，星宇航空消費最高NT$5=1哩，商務旅行優惠',
    issuing_bank: BANK_NAMES[BANK_KEYS.ESUN],
    payment_network: 'VISA',
    picture: 'logo_esun-starlux-business.png',
    key: 'esun-starlux-business',
    link: cardLinkMap[CARD_KEYS.ESUN_STARLUX_BUSINESS].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.ESUN_STARLUX_BUSINESS].applyUrl,
    features: [
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$25=1哩',
        max_feedback: '無上限',
        simplified_condition: '國外消費',
        condition: '國外消費每NT$25可獲得1哩星宇航空COSMILE哩程，活動期間2025/3/1~2025/12/31',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$15=1哩',
        max_feedback: '無上限',
        simplified_condition: '國內消費',
        condition: '國內消費每NT$15可獲得1哩星宇航空COSMILE哩程，活動期間2025/3/1~2025/12/31',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.MILEAGE,
        feedback: '生日月2倍哩程',
        max_feedback: '無上限',
        simplified_condition: '生日月消費',
        condition: '持卡人於生日月份刷卡消費可獲得2倍哩程回饋，活動期間2025/3/1~2025/12/31',
        channel: '生日月消費',
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場貴賓室',
        max_feedback: '每年2次',
        simplified_condition: '消費達門檻',
        condition: '每年最多2次免費使用機場貴賓室服務，須達消費門檻',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場外圍停車',
        max_feedback: '每年4次',
        simplified_condition: '每次最多7天',
        condition: '每年最多4次，每次最多7天免費機場外圍停車服務，須達消費門檻',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: '旅遊平安險',
        max_feedback: '最高3,000萬',
        simplified_condition: '公共運輸工具',
        condition:
          '支付旅途中全額公共運輸工具費用，或參加旅行團時刷卡支付80%以上之團費，享旅遊平安險最高3,000萬保障',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRAVEL],
      },
    ],
    domestic_feedback: 'NT$15=1哩',
    international_feedback: 'NT$25=1哩',
    specific_channel_feedback: 'NT$15=1哩',
    start_date: '2025-03-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.ESUN_STARLUX_WORLD]: {
    name: '玉山星宇航空聯名世界卡',
    summary: '最優NT$10=1哩，星宇航空消費最高NT$5=1哩，頂級旅遊禮遇',
    issuing_bank: BANK_NAMES[BANK_KEYS.ESUN],
    payment_network: 'VISA',
    picture: 'logo_esun-starlux-world.png',
    key: 'esun-starlux-world',
    link: cardLinkMap[CARD_KEYS.ESUN_STARLUX_WORLD].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.ESUN_STARLUX_WORLD].applyUrl,
    features: [
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$10=1哩',
        max_feedback: '無上限',
        simplified_condition: '國外消費',
        condition: '國外消費每NT$10可獲得1哩星宇航空COSMILE哩程，活動期間2025/3/1~2025/12/31',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$20=1哩',
        max_feedback: '無上限',
        simplified_condition: '國內消費',
        condition: '國內消費每NT$20可獲得1哩星宇航空COSMILE哩程，活動期間2025/3/1~2025/12/31',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.MILEAGE,
        feedback: '生日月2倍哩程',
        max_feedback: '無上限',
        simplified_condition: '生日月消費',
        condition: '持卡人於生日月份刷卡消費可獲得2倍哩程回饋，活動期間2025/3/1~2025/12/31',
        channel: '生日月消費',
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場貴賓室',
        max_feedback: '每年12次',
        simplified_condition: '消費達門檻',
        condition: '每年最多12次免費使用機場貴賓室服務，須達消費門檻',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場接送',
        max_feedback: '每年4次',
        simplified_condition: '消費達門檻',
        condition: '每年最多4次免費機場接送服務，須達消費門檻',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場外圍停車',
        max_feedback: '不限次數',
        simplified_condition: '每次最多30天',
        condition: '每年不限次數，每次最多30天免費機場外圍停車服務，須達消費門檻',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: '旅遊平安險',
        max_feedback: '最高5,000萬',
        simplified_condition: '公共運輸工具',
        condition:
          '支付旅途中全額公共運輸工具費用，或參加旅行團時刷卡支付80%以上之團費，享旅遊平安險最高5,000萬保障',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRAVEL],
      },
    ],
    domestic_feedback: 'NT$20=1哩',
    international_feedback: 'NT$10=1哩',
    specific_channel_feedback: 'NT$10=1哩',
    start_date: '2025-03-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.ESUN_STARLUX_WORLD_SUPREME]: {
    name: '玉山星宇航空聯名世界之極卡',
    summary: '最優NT$10=1哩，星宇航空消費最高NT$5=1哩，邀請制頂級卡',
    issuing_bank: BANK_NAMES[BANK_KEYS.ESUN],
    payment_network: 'VISA',
    picture: 'logo_esun-starlux-world-supreme.png',
    key: 'esun-starlux-world-supreme',
    link: cardLinkMap[CARD_KEYS.ESUN_STARLUX_WORLD_SUPREME].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.ESUN_STARLUX_WORLD_SUPREME].applyUrl,
    features: [
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$10=1哩',
        max_feedback: '無上限',
        simplified_condition: '國外消費',
        condition: '國外消費每NT$10可獲得1哩星宇航空COSMILE哩程，活動期間2025/3/1~2025/12/31',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$15=1哩',
        max_feedback: '無上限',
        simplified_condition: '國內消費',
        condition: '國內消費每NT$20可獲得1哩星宇航空COSMILE哩程，活動期間2025/3/1~2025/12/31',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.MILEAGE,
        feedback: '生日月2倍哩程',
        max_feedback: '無上限',
        simplified_condition: '生日月消費',
        condition: '持卡人於生日月份刷卡消費可獲得2倍哩程回饋，活動期間2025/3/1~2025/12/31',
        channel: '生日月消費',
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場貴賓室',
        max_feedback: '每年18次',
        simplified_condition: '消費達門檻',
        condition: '每年最多18次免費使用機場貴賓室服務，須達消費門檻',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場接送',
        max_feedback: '每年8次',
        simplified_condition: '消費達門檻',
        condition: '每年最多8次免費機場接送服務，須達消費門檻',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場外圍停車',
        max_feedback: '不限次數',
        simplified_condition: '每次最多30天',
        condition: '每年不限次數，每次最多30天免費機場外圍停車服務，須達消費門檻',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: '旅遊平安險',
        max_feedback: '最高5,000萬',
        simplified_condition: '公共運輸工具',
        condition:
          '支付旅途中全額公共運輸工具費用，或參加旅行團時刷卡支付80%以上之團費，享旅遊平安險最高5,000萬保障',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRAVEL],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: '全球禮賓秘書',
        max_feedback: '24小時服務',
        simplified_condition: '世界之極卡專屬',
        condition: '提供24小時全球禮賓秘書服務，包含旅遊安排、餐廳預訂、禮品採購等專屬服務',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRAVEL],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: '全球急難救援',
        max_feedback: '緊急醫療協助',
        simplified_condition: '世界之極卡專屬',
        condition: '提供全球急難救援服務，包含緊急醫療協助、旅遊緊急支援等',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRAVEL],
      },
    ],
    domestic_feedback: 'NT$15=1哩',
    international_feedback: 'NT$10=1哩',
    specific_channel_feedback: 'NT$10=1哩',
    start_date: '2025-03-01',
    end_date: '2025-12-31',
  },
};
